import { Box, SelectChangeEvent, Typography, Theme, IconButton } from '@mui/material';

import { Dropdown, Icon } from '@verantos-dev/shared-components';
import { DomainType } from '@verantos-dev/types-cohorts';

import { CustomizeButton } from './CustomizeButton';

type CCohortCharacteristicsHeaderProps = {
  theme: Theme;
  cohortMessages: { [key: string]: string };
  ClinicalCharacteristicsDomainOptions: { value: string; displayValue: string }[];
  CharacteristicTypeOptions: { value: string; displayValue: string }[];
  onCharacteristicsChange: (event: SelectChangeEvent<string>) => void;
  selectedCharacteristicsType: string | null;
  onDomainChange: (event: SelectChangeEvent<string>) => void;
  selectedDomain: string | null;
  onShowDataDialog: () => void;
  onCustomizeConceptGroups?: () => void;
  onResetCustomSelection?: () => void;
  hasCustomSelection?: boolean;
  isLoadingCustomData?: boolean;
};

export const CohortCharacteristicsHeader = ({
  theme,
  cohortMessages,
  CharacteristicTypeOptions,
  onCharacteristicsChange,
  selectedCharacteristicsType,
  ClinicalCharacteristicsDomainOptions,
  onDomainChange,
  selectedDomain,
  onShowDataDialog,
  onCustomizeConceptGroups,
  onResetCustomSelection,
  hasCustomSelection = false,
  isLoadingCustomData = false,
}: CCohortCharacteristicsHeaderProps) => {
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5">{cohortMessages.title}</Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          rowGap: theme.spacing(2),
          marginTop: theme.spacing(5),
        }}
      >
        <>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: theme.spacing(2),
            }}
          >
            <Dropdown
              id="clinical-characteristics-types"
              placeholder="Select Type"
              width="small"
              sx={{ height: 40 }}
              label="Types"
              options={CharacteristicTypeOptions}
              onChange={(event) => onCharacteristicsChange(event as SelectChangeEvent<string>)}
              defaultValue={CharacteristicTypeOptions[1].value}
              value={selectedCharacteristicsType}
              disabled={isLoadingCustomData}
            />
            {selectedCharacteristicsType === CharacteristicTypeOptions[0].value && (
              <Dropdown
                label="Domain"
                placeholder="Select Domain"
                id="clinical-characteristics-domain"
                width="small"
                options={ClinicalCharacteristicsDomainOptions}
                onChange={(event) => onDomainChange(event as SelectChangeEvent<string>)}
                value={selectedDomain}
                sx={{ height: 40 }}
                disabled={isLoadingCustomData}
              />
            )}
            {selectedCharacteristicsType === CharacteristicTypeOptions[0].value &&
              selectedDomain &&
              onCustomizeConceptGroups && (
                <CustomizeButton
                  domain={selectedDomain as DomainType}
                  onClick={onCustomizeConceptGroups}
                  disabled={isLoadingCustomData}
                />
              )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            {selectedCharacteristicsType === CharacteristicTypeOptions[0].value &&
              selectedDomain &&
              onResetCustomSelection && (
                <IconButton
                  onClick={onResetCustomSelection}
                  disabled={!hasCustomSelection || isLoadingCustomData}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: hasCustomSelection
                        ? `${theme.palette.action.focus} !important`
                        : 'transparent',
                    },
                  }}
                >
                  <Icon
                    id="resetCharacteristics"
                    icon="resetCharacteristics"
                    width={20}
                    height={20}
                    sx={{
                      color: hasCustomSelection ? theme.palette.text.primary : '#D3D4D7',
                    }}
                  />
                </IconButton>
              )}
            <Box
              id="cohort-characteristics-open-tabular-data"
              onClick={onShowDataDialog}
              sx={{
                cursor: 'pointer',
                borderRadius: '4px',
                padding: theme.spacing(1),
                height: 32,
                '&:hover': { backgroundColor: `${theme.palette.action.focus} !important` },
              }}
            >
              <Icon id="cohort-characteristics-open-chart" icon="showData" width={24} height={24} />
            </Box>
          </Box>
        </>
      </Box>
    </Box>
  );
};
